/* RESPONSIBLE TEAM: team-inbox */
import { type RenderableData } from 'embercom/objects/inbox/renderable-data';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';
import AdminSummary, { type AdminSummaryWireFormat } from 'embercom/objects/inbox/admin-summary';
import TeamSummary, { type TeamSummaryWireFormat } from 'embercom/objects/inbox/team-summary';
import { EntityType } from 'embercom/models/data/entity-types';
import { OutboundInitialPartSenderType } from 'embercom/objects/inbox/outbound-initial-part-sender-types';
import Tag, { type TagWireFormat } from 'embercom/objects/inbox/tag';
import { tracked } from '@glimmer/tracking';

export type SenderSummary = AdminSummary | TeamSummary;
type SenderSummaryWireFormat = AdminSummaryWireFormat | TeamSummaryWireFormat;
import {
  type TranslationQualityFeedbackWireFormat,
  type TranslationQualityFeedback,
  deserializeTranslationQualityFeedbacks,
} from 'embercom/objects/inbox/renderable/translation-quality-feedback-types';

interface ChatWireFormat {
  renderable_type: RenderableType;
  sender_summary: SenderSummaryWireFormat;
  sender_type: OutboundInitialPartSenderType;
  blocks: Array<object>;
  tags: Array<TagWireFormat>;
  ruleset_id?: number;
  selection_set_id?: number;
  admin_summary?: AdminSummaryWireFormat;
  deleted_at?: Date;
  translation_quality_feedbacks?: Record<string, TranslationQualityFeedbackWireFormat>;
  multilingual_workflow_translated?: boolean;
}

export default class Chat implements RenderableData {
  readonly renderableType = RenderableType.Chat;
  readonly senderSummary: SenderSummary;
  readonly senderType: OutboundInitialPartSenderType;
  readonly blocks: Array<object>;
  @tracked tags: Array<Tag>;
  readonly rulesetId?: number;
  readonly selectionSetId?: number;
  readonly adminSummary?: AdminSummary;
  readonly deletedAt?: Date;
  readonly translationQualityFeedbacks?: Record<string, TranslationQualityFeedback>;
  readonly multilingualWorkflowTranslated?: boolean;

  get teammate() {
    if (this.senderType === OutboundInitialPartSenderType.Admin) {
      return this.senderSummary;
    } else {
      return;
    }
  }

  get team() {
    if (this.senderType === OutboundInitialPartSenderType.Team) {
      return this.senderSummary;
    } else {
      return;
    }
  }

  get creatingEntity() {
    if (this.senderType === OutboundInitialPartSenderType.Bot) {
      return { type: EntityType.Bot, id: -1 };
    } else if (this.team) {
      return { type: EntityType.Team, id: this.senderSummary.id, object: this.team };
    } else {
      return { type: EntityType.Admin, id: this.senderSummary.id, object: this.teammate };
    }
  }

  get styles() {
    let styles = {
      textColor: 'text-default',
      backgroundColor: 'bg-beta-container',
    };
    if (this.senderType === OutboundInitialPartSenderType.Bot) {
      styles.backgroundColor = 'bg-beta-container';
    }

    return {
      ...styles,
      backgroundColorHover: 'bg-beta-container-emphasis',
    };
  }

  constructor(
    blocks: Array<object>,
    senderSummary: SenderSummary,
    senderType: OutboundInitialPartSenderType,
    tags?: Array<Tag>,
    rulesetId?: number,
    selectionSetId?: number,
    adminSummary?: AdminSummary,
    deletedAt?: Date,
    translationQualityFeedbacks?: Record<string, TranslationQualityFeedback>,
    multilingualWorkflowTranslated?: boolean,
  ) {
    this.blocks = blocks;
    this.senderSummary = senderSummary;
    this.senderType = senderType;
    this.tags = tags || [];
    this.rulesetId = rulesetId;
    this.selectionSetId = selectionSetId;
    this.adminSummary = adminSummary;
    this.deletedAt = deletedAt;
    this.translationQualityFeedbacks = translationQualityFeedbacks;
    this.multilingualWorkflowTranslated = multilingualWorkflowTranslated;
  }

  static deserialize(json: ChatWireFormat): Chat {
    let senderSummary;
    if (json.sender_type === OutboundInitialPartSenderType.Team) {
      senderSummary = TeamSummary.deserialize(json.sender_summary as TeamSummaryWireFormat);
    } else {
      senderSummary = AdminSummary.deserialize(json.sender_summary as AdminSummaryWireFormat);
    }
    let adminSummary;
    if (json.admin_summary) {
      adminSummary = AdminSummary.deserialize(json.admin_summary);
    }
    let translationQualityFeedbacks = deserializeTranslationQualityFeedbacks(
      json.translation_quality_feedbacks,
    );

    return new Chat(
      json.blocks,
      senderSummary,
      json.sender_type,
      json.tags && json.tags.map(Tag.deserialize),
      json.ruleset_id,
      json.selection_set_id,
      adminSummary,
      json.deleted_at,
      translationQualityFeedbacks,
      json.multilingual_workflow_translated,
    );
  }
}
